import React from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, ClipboardList, Mail, ArrowRight } from "lucide-react";
import CfriShell from "./CfriShell";
import { LandscapeChart, QuadrantChart } from "./CfriFigures";
import { MapFrame, PaperLink, ProvenanceTag, fmt } from "./CfriUI";
import { SectionCard, MetricCard } from "../funder/FunderUI";

export default function CfriProposal() {
    const { id } = useParams();
    return (
        <CfriShell active={id}>
            {(content) => <Proposal content={content} id={id} />}
        </CfriShell>
    );
}

function formatSubmitted(iso) {
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function Proposal({ content, id }) {
    const navigate = useNavigate();
    const list = content.proposals;
    const idx = list.findIndex((p) => p.id === id);
    if (idx < 0) return <Navigate to="/cfri" replace />;

    const p = list[idx];
    const prev = idx > 0 ? list[idx - 1] : null;
    const next = idx < list.length - 1 ? list[idx + 1] : null;
    const quadrant = content.figures.quadrant.proposals.find((q) => q.id === p.id);
    const computedNotes = p.notes.filter((n) => n.kind !== "engine");
    const engineNotes = p.notes.filter((n) => n.kind === "engine");
    const open = (pid) => navigate(`/cfri/proposal/${pid}`);

    return (
        <div className="funder-section-stack">
            <div className="cfri-no-print" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <span className="funder-link" onClick={() => navigate("/cfri")}><ArrowLeft size={14} /> All proposals</span>
                <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
                    <button className="funder-btn-ghost" onClick={() => navigate(`/cfri/briefing/${p.id}`)}>
                        <ClipboardList size={14} /> Committee briefing
                    </button>
                    <button className="funder-btn-ghost" disabled={!prev} onClick={() => prev && open(prev.id)} style={{ opacity: prev ? 1 : 0.4 }} aria-label="Previous proposal" title={prev ? prev.shortName : undefined}><ChevronLeft size={14} /></button>
                    <button className="funder-btn-ghost" disabled={!next} onClick={() => next && open(next.id)} style={{ opacity: next ? 1 : 0.4 }} aria-label="Next proposal" title={next ? next.shortName : undefined}><ChevronRight size={14} /></button>
                </div>
            </div>

            <div>
                <div className="funder-grant-no" style={{ marginBottom: 4, whiteSpace: "normal" }}>{p.program} · submitted {formatSubmitted(p.submitted)}</div>
                <h1 className="funder-h1" style={{ color: "var(--ink-900)", margin: "0 0 6px" }}>{p.title}</h1>
                <div style={{ color: "var(--ink-500)", fontSize: 13.5 }}>{p.applicant}{p.institution ? ` · ${p.institution}` : ""}</div>
            </div>

            <div className="funder-metric-grid cfri-metric-grid">
                <MetricCard label="CF papers close to it" value={fmt(p.glance.papersClose)} sub={quadrant ? `${quadrant.early} in 2016–20 · ${quadrant.late} in 2021–25` : undefined} />
                <MetricCard label="Published near it since submission" value={fmt(p.glance.sinceSubmission)} sub={`Since ${formatSubmitted(p.submitted)}`} />
                <MetricCard label="Its corner of CF research" sub={quadrant?.reading}>
                    <div className="cfri-metric-text">{p.glance.corner.charAt(0).toUpperCase() + p.glance.corner.slice(1)}</div>
                </MetricCard>
                <MetricCard label="Reviewers suggested" value={p.glance.reviewers.total} sub={`${p.glance.reviewers.withEmail} with an email on file`} />
            </div>

            <div className="funder-grid-proposal">
                <div className="funder-section-stack" style={{ minWidth: 0 }}>
                    {engineNotes.map((n, i) => (
                        <div key={i} className="funder-card cfri-probe">
                            <div className="funder-card-body">
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                                    <ProvenanceTag kind="engine" />
                                    <strong style={{ fontSize: 14, color: "var(--ink-900)" }}>To probe in review</strong>
                                </div>
                                {n.questions ? (
                                    <ol className="cfri-questions">
                                        {n.questions.map((q, j) => <li key={j}>{q}</li>)}
                                    </ol>
                                ) : <p className="cfri-body-text" style={{ margin: 0 }}>{n.text}</p>}
                            </div>
                        </div>
                    ))}

                    <SectionCard kicker="Since it was written" title="What the literature says now">
                        {computedNotes.map((n, i) => (
                            <div key={i} className="cfri-note">
                                <ProvenanceTag kind={n.kind} />
                                <p className="cfri-body-text">{n.text}</p>
                            </div>
                        ))}
                    </SectionCard>

                    <SectionCard kicker="Closest published work" title="The papers nearest this proposal's text" right={<ProvenanceTag kind="computed" />}>
                        <div className="cfri-stack-wrap">
                            <table className="funder-table cfri-table cfri-stack-sm">
                                <thead>
                                    <tr><th>Paper</th><th className="cfri-num">Year</th><th className="cfri-num" title="Cosine similarity between the proposal's text and the paper's">Match</th></tr>
                                </thead>
                                <tbody>
                                    {p.closest.map((c) => (
                                        <tr key={c.url}>
                                            <td><PaperLink paper={c} showYear={false} /></td>
                                            <td className="cfri-num">{c.year}</td>
                                            <td className="cfri-num" data-label="match">{c.match.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </SectionCard>

                    <SectionCard kicker="Assessment" title="Read against today's literature" subtitle={p.assessmentNote} right={<ProvenanceTag kind="engine" />}>
                        {p.assessment.map((a) => (
                            <div key={a.dimension} className="cfri-assessment">
                                <div className="cfri-assessment-dim">{a.dimension}</div>
                                <p className="cfri-body-text">{a.text}</p>
                            </div>
                        ))}
                    </SectionCard>

                    <SectionCard kicker="Suggested reviewers" title="Who could review it" subtitle={content.method.items.find((m) => m.label === "Reviewers")?.text} right={<ProvenanceTag kind="computed" />}>
                        {p.reviewerGroups.map((g) => (
                            <div key={g.label} className="cfri-reviewer-group">
                                <div className="funder-kicker">{g.label}</div>
                                {g.reviewers.map((r) => (
                                    <div key={r.name} className="funder-list-row">
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div className="funder-list-title">{r.name}</div>
                                            {r.institution && <div className="funder-list-detail">{r.institution}</div>}
                                            {r.paper && (
                                                <div style={{ fontSize: 12.5, marginTop: 4 }}>
                                                    <span className="cfri-muted">Closest paper: </span><PaperLink paper={r.paper} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="cfri-contact">
                                            {r.email
                                                ? <a href={`mailto:${r.email}`} className="cfri-email"><Mail size={12} /> {r.email}</a>
                                                : <span className="cfri-muted">No email on file</span>}
                                            {r.orcid && <a href={r.orcid} target="_blank" rel="noopener noreferrer" className="cfri-orcid">ORCID</a>}
                                        </div>
                                    </div>
                                ))}
                                {g.backups.length > 0 && (
                                    <p className="cfri-backups">
                                        <span className="cfri-muted">Backups: </span>
                                        {g.backups.map((b, i) => (
                                            <span key={b.name}>
                                                {i > 0 && ", "}
                                                {b.email ? <a href={`mailto:${b.email}`}>{b.name}</a> : b.name}
                                                {!b.email && <span className="cfri-muted"> (no email)</span>}
                                            </span>
                                        ))}
                                    </p>
                                )}
                            </div>
                        ))}
                    </SectionCard>
                </div>

                <div className="funder-section-stack" style={{ minWidth: 0 }}>
                    <div className="funder-card cfri-briefing-cta">
                        <div className="funder-card-body">
                            <div className="funder-kicker">For the committee</div>
                            <p className="cfri-body-text" style={{ margin: "0 0 12px" }}>
                                A one-page briefing with this proposal's terms, numbers and closest work in plain language, for committee members who are not scientists.
                            </p>
                            <button className="funder-btn" onClick={() => navigate(`/cfri/briefing/${p.id}`)}>
                                Open the briefing <ArrowRight size={14} />
                            </button>
                        </div>
                    </div>

                    <SectionCard kicker="Where it sits" title="Crowded and growing" subtitle={quadrant?.reading}>
                        <QuadrantChart data={content.figures.quadrant} focusId={p.id} onSelectProposal={open} compact />
                    </SectionCard>

                    <SectionCard kicker="Its research areas" title="Joined to the areas its text is closest to">
                        <LandscapeChart data={content.figures.landscape} focusId={p.id} onSelectProposal={open} compact />
                    </SectionCard>
                </div>
            </div>

            <SectionCard kicker="Advanced" title="The interactive map" subtitle={p.mapNote}>
                <MapFrame id={p.id} title={`Interactive map for ${p.shortName}`} />
            </SectionCard>

            <p className="cfri-footnote">{content.overview.disclaimer} <span className="funder-link" onClick={() => navigate("/cfri/method")}>How this was computed</span></p>
        </div>
    );
}
